// eslint-disable-next-line import/no-webpack-loader-syntax
import url from 'worklet-loader!./AudioRecorder.js';

import {CustomAudioBuffer} from './CustomAudioBuffer';
import {CustomRecorder} from './CustomRecorder';
import {MusicTranscriptionWorkerController} from './MusicTranscriptionWorkerController';
import {UserMedia} from './UserMedia';
import {MidiKeyEvent} from '../midi/models/MidiKeyEvent.ts';
// eslint-disable-next-line import/named
import {NoteEventTime} from '@spotify/basic-pitch';

import {TranscriptionEvent} from './models/TranscriptionEvent';
import Logger, {LogLevel} from './logger/Logger';

import {userService} from '../user/UserService';
import {type SetTranscriptionParamsMessage, WorkerMessageType} from './models/WorkerMessageType';

import {Frequency} from 'tone';
import  {type TranscriberState, type Transcription, TranscriptionMessageType} from './models/TranscriptionMessageTypes';
import Vue from 'vue';
import { analyticsController } from '../user/analytics/AnalyticsController.ts';

import { musicTranscriptionTimer } from '.';


type AvailableWorkerState = 'notReady' | 'ready' | 'loading'
type AvailablePerformanceTestValid = 'notMade' | 'validated' | 'notValidated' | 'reported' | 'notAllowed';
type AvailableRecorderState = 'recording' | 'paused' | 'off';
interface MusicTranscriptionControllerState {
  workerState: AvailableWorkerState, 
  isPerformanceTestProcessing: boolean, 
  performanceTestValid: AvailablePerformanceTestValid,
  recorderState: AvailableRecorderState
}
interface MusicTranscriptionControllerPerformanceTest {
  optimalPeriod: number | null,
  executedAt: Date,
  status: AvailablePerformanceTestValid
}

export class MusicTranscriptionController extends TranscriptionEvent {
  private userMedia: UserMedia | null;
  private gain?: GainNode;
  private gainValue: number;
  private recorder?: CustomRecorder;
  private audioBuffer: CustomAudioBuffer | null;

  private musicTranscriptionWorkerController: MusicTranscriptionWorkerController | null;

  private inferenceWindowDuration: number; // How long are the windows used for inference (ms)
  private possibleInferencePeriods: number[];
  private contextSampleRate: number;

  private midiArray: NoteEventTime[][];

  private performanceLogs;

  private logger: Logger;

  public state = Vue.observable<MusicTranscriptionControllerState>({
    workerState: 'notReady',
    isPerformanceTestProcessing: false,
    performanceTestValid: 'notMade',
    recorderState : 'off',
  })
  /**
   *
   * @param useMode Which use mode to select
   * @param inferenceWindowDuration Duration of the audio windows to compute
   */
  constructor() {
    super();
    this.gainValue = 0.7;
    this.possibleInferencePeriods = [200, 400, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000];
    this.logger = Logger.getInstance();

    this.musicTranscriptionWorkerController = null;

    this.state.workerState = 'notReady'
    const audioCapturePerformanceOptimalPeriod: MusicTranscriptionControllerPerformanceTest | undefined  = localStorage.audioCapturePerformanceOptimalPeriod && JSON.parse(localStorage.audioCapturePerformanceOptimalPeriod)
    this.state.performanceTestValid = audioCapturePerformanceOptimalPeriod ? audioCapturePerformanceOptimalPeriod.status : 'notMade'
    this.state.isPerformanceTestProcessing = false
    this.inferenceWindowDuration = audioCapturePerformanceOptimalPeriod?.optimalPeriod ? audioCapturePerformanceOptimalPeriod.optimalPeriod : 500

    this.performanceLogs = {
      predict: {
        'create converter duration': [],
        'resampling duration': [],
        'input data length': [],
        'resampled data length': [],
        'inference duration': [],
        'frames to time duration': [],
        'input buffer': [],
        'output buffer': [],
      },
    };

    this.midiArray = [];

    // Instantiate a new worker
    // this.instanciateWorker();
  }

  public getWindowInferenceDuration(): number {
    return this.inferenceWindowDuration;
  }

  private setWindowInferenceDuration(duration: number) {
    this.inferenceWindowDuration = duration;
    this.audioBuffer?.setDuration(duration);
  }

  private instanciateWorker() {
    if (!this.musicTranscriptionWorkerController) {
      this.state.workerState = 'loading'
      this.musicTranscriptionWorkerController = new MusicTranscriptionWorkerController((event: MessageEvent<any>) => {
        const {type, data} = event.data;

        switch (type) {
          case WorkerMessageType.InitMsg:
            if (data.isReady) {
              this.state.workerState = 'ready'
              this.dispatchEvent({
                type: TranscriptionMessageType.InitMsg,
                isReady: data.isReady,
              } as TranscriberState);
            }
            break;
          case WorkerMessageType.AudioBuffer:
            // Performance Logs
            this.performanceLogs.predict['create converter duration'].push(
              data.performanceLogs.predict['create converter duration']
            );
            this.performanceLogs.predict['resampling duration'].push(
              data.performanceLogs.predict['resampling duration']
            );
            this.performanceLogs.predict['input data length'].push(data.performanceLogs.predict['input data length']);
            this.performanceLogs.predict['resampled data length'].push(
              data.performanceLogs.predict['resampled data length']
            );
            this.performanceLogs.predict['inference duration'].push(data.performanceLogs.predict['inference duration']);
            this.performanceLogs.predict['frames to time duration'].push(
              data.performanceLogs.predict['frames to time duration']
            );
            this.performanceLogs.predict['input buffer'].push(data.performanceLogs.predict['input buffer']);
            this.performanceLogs.predict['output buffer'].push(data.performanceLogs.predict['output buffer']);

            this.performanceLogs.warmup = data.performanceLogs.warmup;

            // Event data
            console.log(data.midiArray);
            this.midiArray.push(data.midiArray); // Store MIDI data into local array (in receiving order)
            this.dispatchEvent({
              type: TranscriptionMessageType.Transcription,
              frameID: 0,
              midiData: data.midiArray,
            } as Transcription);
            break;
        }
      });
    }
  }

  public getFrequencyRangeFromDivisions(divisions: any) {
    let _minLeftKey = 88;
    let _maxLeftKey = 0;
    let _minRightKey = 88;
    let _maxRightKey = 0;

    // Loop over
    divisions.forEach((division) => {
      const currentHandprint = division.currentHandprint;
      currentHandprint.forEach((handprint) => {
        if (handprint.hand === 'RIGHT') {
          if (handprint.key < _minRightKey) {
            _minRightKey = handprint.key;
          }
          else if (handprint.key > _maxRightKey) {
            _maxRightKey = handprint.key;
          }
        }
        else if (handprint.hand === 'LEFT') {
          if (handprint.key < _minLeftKey) {
            _minLeftKey = handprint.key;
          }
          else if (handprint.key > _maxLeftKey) {
            _maxLeftKey = handprint.key;
          }
        }
      });
    });

    const shiftPiano = 20;
    _minLeftKey += shiftPiano;
    _minRightKey += shiftPiano;
    _maxLeftKey += shiftPiano;
    _maxRightKey += shiftPiano;

    return {_minLeftKey, _maxLeftKey, _minRightKey, _maxRightKey};
  }

  public setTranscriptionFrequencyRange(keyRange: any, handType: string, tolerance: number = 6) {
    let {_minLeftKey, _maxLeftKey, _minRightKey, _maxRightKey} = keyRange;
    // Apply tolerance if possible
    if (_minLeftKey - tolerance >= 0) {
      _minLeftKey -= tolerance;
    }
    if (_maxLeftKey + tolerance < 88) {
      _maxLeftKey += tolerance;
    }
    if (_minRightKey - tolerance >= 0) {
      _minRightKey -= tolerance;
    }
    if (_maxRightKey + tolerance < 88) {
      _maxRightKey += tolerance;
    }

    // Select correct range
    let _minKey = 0;
    let _maxKey = 88;
    if (handType === 'LEFT_HAND') {
      _minKey = _minLeftKey;
      _maxKey = _maxLeftKey;
    }
    else if (handType === 'RIGHT_HAND') {
      _minKey = _minRightKey;
      _maxKey = _maxRightKey;
    }
    else if (handType === 'BOTH_HAND') {
      _minKey = Math.min(_minLeftKey, _minRightKey);
      _maxKey = Math.max(_maxLeftKey, _maxRightKey);
    }

    // Convert keys to frequencies
    const _minFreq = Frequency(_minKey, 'midi').toFrequency();
    const _maxFreq = Frequency(_maxKey, 'midi').toFrequency();

    const message: SetTranscriptionParamsMessage = {
      onsetThresh: 0.5,
      frameThresh: 0.25,
      minNoteLen: 3,
      inferOnsets: true,
      maxFreq: _maxFreq,
      minFreq: _minFreq,
      melodiaTrick: true,
    };
    this.musicTranscriptionWorkerController?.post(WorkerMessageType.SetTranscriptionParams, message);
  }

  private async registerAudioPipeline() {
    if (!this.userMedia) {
      this.userMedia = new UserMedia();
    }
    
    await this.userMedia.open();
    this.contextSampleRate = this.userMedia.sampleRate;
    // Instantiate the audio buffer
    this.createAudioBuffer();

    // Register gainNode
    this.gain = this.userMedia.createGain();
    this.gain.gain.setValueAtTime(this.gainValue, this.userMedia.currentTime);
    this.userMedia.mediaStream?.connect(this.gain);

    // Register custom audio recorder
    await this.userMedia.audioWorklet.addModule(url); // Gather custom processor
    this.recorder = new CustomRecorder(this.userMedia);
    this.recorder.port.addEventListener('message', (event) => {
      // Send audio to buffer
      this.audioBuffer?.update(event.data.output[0], (data, sr) => {
        this.musicTranscriptionWorkerController?.post(WorkerMessageType.AudioBuffer, {
          audioBuffer: data,
          sampleRate: sr,
        });
      });
    });
    this.recorder.port.start();
    this.userMedia.mediaStream?.connect(this.recorder);
  }

  private createAudioBuffer() {
    if (this.audioBuffer) {
      this.audioBuffer = null;
    }
    this.audioBuffer = new CustomAudioBuffer(this.inferenceWindowDuration, this.contextSampleRate);
  }

  public async init(period = 1000): Promise<this | 'notAllowed'> {
    // Useless right now
    try {
      this.inferenceWindowDuration = period;
      await this.registerAudioPipeline();
      this.instanciateWorker();
  
      return this;
    } catch (error) {
      if(error.name === 'NotAllowedError') {
        analyticsController.logBasicAnalyticsEvent('refused microphone authorization', 'audio capture usage', {error: error.message});

        if(localStorage.audioCapturePerformanceOptimalPeriod) {
          const audioCapturePerformanceOptimalPeriod = JSON.parse(localStorage.audioCapturePerformanceOptimalPeriod)
          localStorage.audioCapturePerformanceOptimalPeriod = JSON.stringify({...audioCapturePerformanceOptimalPeriod, status: 'notAllowed'})
          this.state.performanceTestValid = 'notAllowed'
        }

        return 'notAllowed'
      }

      throw error
    }
  }

  public getMidi() {
    return this.midiArray.slice();
  }

  private waitForTranscriptionEvent = (element: TranscriptionEvent): Promise<Event> => {
    return new Promise((resolve) => {
      const handler = (event: Event) => {
        resolve(event);
      };
      element.addEventListener(handler);
    });
  };

  public midiToDiv(
    divTimeArray: number[],
    startDivision: number,
    startDivisionTime: number,
    timerStartsAt: number,
    recordStartsAt: number
  ) {
    console.log({
      "divTimeArray": divTimeArray,
      "startDivision": startDivision,
      "startDivisionTime": startDivisionTime,
      "timerStartsAt": timerStartsAt,
      "recordStartsAt": recordStartsAt
    })
    const binarySearch = (timeArray: number[], target: number) => {
      let low = 0;
      let high = timeArray.length - 1;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        if (timeArray[mid] === target) {
          return mid;
        }
        else if (timeArray[mid] < target) {
          low = mid + 1;
        }
        else {
          high = mid - 1;
        }
      }

      return low - 1;
    };

    // divTimeArray.unshift(0); // Add 0 value at the begining of the array
    const earlyShiftDuration = timerStartsAt - recordStartsAt;
    const divisionMap = new Map<number, MidiKeyEvent[]>();
    const midiShift = 20;
    const firstDivisionIndex = binarySearch(divTimeArray, startDivisionTime - earlyShiftDuration);

    // Loop over the transcribed array once and interpolate each time segment with the correct division coverage
    let segmentIndex = 0;
    this.midiArray.forEach((segment) => {
      const segmentStartTime = segmentIndex * this.inferenceWindowDuration;
      segment.forEach((eventTime: NoteEventTime) => {
        const eventStartTime = eventTime.startTimeSeconds * 1000 + segmentStartTime;
        const eventStopTime = (eventTime.startTimeSeconds + eventTime.durationSeconds) * 1000 + segmentStartTime;
        const eventStartDivision =
          binarySearch(divTimeArray, eventStartTime - earlyShiftDuration) - firstDivisionIndex + startDivision - 1;
        const eventStopDivision =
          binarySearch(divTimeArray, eventStopTime - earlyShiftDuration) - firstDivisionIndex + startDivision - 1;
        console.log(
          'Current start/stop divisions are:',
          eventStartDivision,
          eventStopDivision,
          eventStartTime,
          eventStopTime
        );

        // Fill the final result
        for (let div = eventStartDivision; div <= eventStopDivision; div++) {
          if (!divisionMap.has(div)) {
            divisionMap.set(div, [new MidiKeyEvent(eventTime.pitchMidi - midiShift)]);
          }
          else {
            const tmp: any = divisionMap.get(div);
            let includes = false;
            tmp.forEach((midiKeyEvent) => {
              if (midiKeyEvent.key === eventTime.pitchMidi - midiShift) {
                includes = true;
              }
            });
            if (!includes) {
              tmp.push(new MidiKeyEvent(eventTime.pitchMidi - midiShift));
            }
          }
        }
      });

      segmentIndex += 1;
    });

    return divisionMap;
  }

  /**
   * Start the transcription system if ready
   */
  public listen(): TranscriptionEvent {
    this.audioBuffer?.clear(); // Clear audio buffer
    this.midiArray.length = 0; // Clear MIDI data
    this.logger.clearLogs();
    this.recorder?.start();

    this.state.recorderState = 'recording';

    return this;
  }

  public stopListening() {
    this.recorder?.stop();

    this.state.recorderState = 'off';

    /**
     * Logs necessary entries, with custom json structure
     */
    // WORKER DATA
    this.logger.log(
      'create_converter_duration',
      LogLevel.TRACE,
      this.performanceLogs.predict['create converter duration']
    );
    this.logger.log('resampling', LogLevel.TRACE, {
      'duration': this.performanceLogs.predict['resampling duration'],
      'input length': this.performanceLogs.predict['input data length'],
      'output length': this.performanceLogs.predict['resampled data length'],
      'input_sr': this.performanceLogs.predict['input sr'],
      'output_sr': this.performanceLogs.predict['output sr'],
    });
    this.logger.log('inference_duration', LogLevel.TRACE, this.performanceLogs.predict['inference duration']);
    this.logger.log('frames_to_time_duration', LogLevel.TRACE, this.performanceLogs.predict['frames to time duration']);
    this.logger.log('backend', LogLevel.TRACE, this.performanceLogs.warmup);
    const audioTracks = this.userMedia?.mediaStream?.mediaStream.getAudioTracks() || [];
    const tracksConstraints = {};
    audioTracks.forEach((track) => {
      tracksConstraints[track.id] = track.getConstraints();
    });
    this.logger.log('tracks_constraints', LogLevel.TRACE, tracksConstraints);
    if (userService.user.configurations.isAudioLogsCollectionAuthorized) {
      console.info('Audio buffer has been included in logs');
      this.logger.log('audio_buffer', LogLevel.TRACE, {
        // "input_buffer": this.performanceLogs.predict['input buffer'],
        output_buffer: this.performanceLogs.predict['output buffer'],
      });
    }
    else {
      console.info('Audio buffers has been removed from logs');
    }
    // Log various information
    this.logger.log('environment', LogLevel.TRACE, {
      user_agent: navigator.userAgent,
      gpu: navigator.gpu,
    });
  }

  public pause() {
    try {
      this.userMedia?.mediaStream?.mediaStream.getTracks().forEach((track) => {
        track.stop();
      })
      this.userMedia?.close().then(() => {
        this.userMedia=null;
      })
    }
    catch (err) {

    }
  }

  public resume() {
    this.registerAudioPipeline();
  }

  public terminate(): void {
    if (this.musicTranscriptionWorkerController) {
      this.musicTranscriptionWorkerController.terminate();
      this.musicTranscriptionWorkerController = null;
      this.state.workerState = 'notReady';
    }
    this.pause();
    this.userMedia?.close().then(() => {
      this.userMedia=null;
    })
  }

  private async getPerformance(): Promise<number | null | 'notAllowed'> {
    if (this.musicTranscriptionWorkerController) {
      // Kill the worker if already existing
      this.terminate();
    }

    const genSinWave = (f0: number, sr: number, durationMs: number): number[] => {
      const durationSeconds = durationMs / 1000;
      const nSamples = Math.floor(sr * durationSeconds);
      const signal: number[] = [];

      for (let i = 0; i < nSamples; i++) {
        const t = i / sr;
        signal.push(Math.sin(2 * Math.PI * f0 * t));
      }

      return signal;
    };

    // Best period search algorithm
    const condition = async (period: number): Promise<Boolean> => {
      // Generate a sin wave with the given period as length
      const sinWave = genSinWave(440, this.contextSampleRate, period);

      // Prepare for the transcription
      this.midiArray.length = 0; // Clear MIDI data
      this.logger.clearLogs();
      // Create audioBuffer with current parameter
      this.setWindowInferenceDuration(period);
      this.createAudioBuffer();

      // Fill the buffer and trigger the transcription
      this.audioBuffer?.update(sinWave, (data, sr) => {
        this.musicTranscriptionWorkerController?.post(WorkerMessageType.AudioBuffer, {
          audioBuffer: data,
          sampleRate: sr,
        });
      });

      let inferenceDuration: number = 10000;
      await this.waitForTranscriptionEvent(this);
      const inferenceDurationArray = this.performanceLogs.predict['inference duration'];
      inferenceDuration = inferenceDurationArray[inferenceDurationArray.length - 1];

      // Compute the final condition
      console.log('Inference duration is:', inferenceDuration);
      const inferenceRatio = inferenceDuration / period;
      const maxRatio = 0.9;
      if (inferenceRatio >= maxRatio) {
        return false;
      }
      else {
        return true;
      }
    };

    const findOptimalPeriod = async (periods: number[]): Promise<number | null | 'notAllowed'> => {
      let left = 0;
      let right = periods.length - 1;
      let bestPeriod: number | null = null;

      while (left <= right) {
        // Binary search
        const mid = Math.floor((left + right) / 2);
        const currentPeriod = periods[mid];

        const conditionSatisfied = await Promise.race([
          condition(currentPeriod),
          new Promise(resolve => setTimeout(() => resolve(false), currentPeriod + 10)) // Stop the current step if exceeding the current period by 10ms
        ]);

        if (conditionSatisfied) {
          bestPeriod = currentPeriod; // Current period satisfies the condition
          right = mid - 1; // Try to find a better (lower) period
        }
        else {
          left = mid + 1; // Move to the higher half since condition isn't satisfied
        }
      }

      return bestPeriod;
    };

    console.log('Init webworker');

    let initTimeoutId;
    try {
      let timeoutId;
      const response = await Promise.race([
        this.init().then((res) => {
          clearTimeout(initTimeoutId);
          return res;
        }),
        new Promise((_, reject) =>
          initTimeoutId = setTimeout(() => {
            analyticsController.logBasicAnalyticsEvent('failed to initialize tensorflow backend', 'audio capture performance test');
            reject(new Error('timeout'))
          }, 10000) // Return null if the init doesn't end before 10s
        ),
      ]).catch(err => {
        if (err.message === 'timeout') {
          if (this.musicTranscriptionWorkerController) {
            // Kill the worker if already existing
            this.terminate();
          }
          return null;
        }

        throw err;
      });

      if(response === 'notAllowed') {
        return 'notAllowed'
      }

      await this.waitForTranscriptionEvent(this);
      const optimalPeriod = await findOptimalPeriod(this.possibleInferencePeriods);
      
      this.terminate()
  
      return optimalPeriod;
    } catch (error) {
      analyticsController.logBasicAnalyticsEvent('performance test failed', 'audio capture performance test', { error: error.message });
    }


    return null
  }

  public async executePerformanceTest() {
    if(this.state.isPerformanceTestProcessing) {
      return
    }

    this.state.isPerformanceTestProcessing = true
      
    // If the total test duration exceeds 25s, return null
    let globalTimeoutId;
    const findedOptimalPeriod = await Promise.race([
      this.getPerformance().then((res) => {
        clearTimeout(globalTimeoutId);
        return res;
      }),
      new Promise<null>((resolve) => {
        globalTimeoutId = setTimeout(() => {
          analyticsController.logBasicAnalyticsEvent('performance test has timed out', 'audio capture performance test');
          resolve(null);
        }, 25000);
      }),
    ]);
    
    const optimalPeriod = findedOptimalPeriod === 'notAllowed' ? null : findedOptimalPeriod

    analyticsController.logBasicAnalyticsEvent('ended audio performance test', 'audio capture performance test', { value: optimalPeriod })
    this.state.performanceTestValid = typeof findedOptimalPeriod === 'number' ? 'validated' : findedOptimalPeriod === 'notAllowed' ? 'notAllowed' : 'notValidated'
    localStorage.audioCapturePerformanceOptimalPeriod = JSON.stringify({ optimalPeriod, status: this.state.performanceTestValid, executedAt: new Date() } as MusicTranscriptionControllerPerformanceTest)
    this.state.isPerformanceTestProcessing = false

    return findedOptimalPeriod
  }
}
